<script setup lang="ts">
  import type { ITabs, ISection, ISectionItem, ISectionChildItem } from '@/components/main-header/types'
  import { useMainHeaderStore } from '@/components/main-header/store'

  const props = defineProps<{ sidebarData: { tabs: ITabs[]; sections: ISection[] } }>()

  const emit = defineEmits(['close'])

  const { locale } = useI18n()

  const navigationLevel = ref(0)
  function stepBack() {
    if (!navigationLevel.value) {
      return
    }

    navigationLevel.value -= 1
  }

  function stepForward() {
    navigationLevel.value += 1
  }

  const activeSectionItems = ref<(ISectionItem | ISection)[]>(props.sidebarData.sections)
  function setActiveSectionItems(items: (ISectionItem | ISection)[], isBack: boolean = false) {
    isBack ? stepBack() : stepForward()

    activeSectionItems.value = items
  }

  onUnmounted(() => emit('close'))

  const mainHeaderStore = useMainHeaderStore()

  const route = useRoute()
  function onSectionItemClick(item: ISectionChildItem) {
    const [path, hash] = item.href.split('#')
    const isSamePage = path === route.path

    if (isSamePage && hash) {
      emit('close')
    }
  }
</script>
<template>
  <div class="fixed left-0 top-0">
    <UiDarkOverlay
      class="top-0"
      @click="emit('close')"
    />
    <div class="absolute left-0 top-0 flex h-dvh w-74 flex-col bg-white">
      <div class="flex h-full max-h-14 w-full items-center border-b border-b-divider">
        <span
          v-if="navigationLevel === 0"
          tabindex="0"
          class="pl-4"
        >
          <NuxtImg
            v-if="mainHeaderStore.logo?.icon.source"
            :src="mainHeaderStore.logo?.icon.source"
            :alt="mainHeaderStore.logo?.icon.alt"
            class="w-16"
          />
        </span>
        <button
          v-else
          data-test="sidebar-back"
          class="flex items-center gap-4 pl-4"
          @click="setActiveSectionItems(sidebarData.sections, true)"
        >
          <Icon name="common/chevron-left" />
          <span class="text-bt-blue-dark text-subtitle-2">{{ $t('forms.back') }}</span>
        </button>
      </div>
      <div
        data-test="sidebar-items"
        class="h-full overflow-y-auto px-4"
      >
        <NuxtLink
          v-for="tab in sidebarData.tabs"
          :key="tab.key"
          :to="{ name: `index___${locale}`, query: { opentab: tab.key } }"
          class="flex items-center gap-4 border-b border-b-divider py-4 no-underline"
          :aria-labelledby="`tab-${tab.key}`"
          external
          no-prefetch
        >
          <Icon
            :name="tab.icon"
            class="text-bt-blue-dark"
          />
          <p
            :id="`tab-${tab.key}`"
            class="mb-0 text-bt-blue-dark text-body-1"
          >
            {{ tab.label }}
          </p>
        </NuxtLink>
        <div
          v-for="section in activeSectionItems"
          :key="section.label"
          class="border-b border-b-divider py-4 last:border-b-0"
          data-test="sidebar-sections"
        >
          <p class="mb-4 text-secondary text-subtitle-2">{{ section.label }}</p>
          <div class="flex flex-col gap-4">
            <div
              v-for="item in section.items"
              :key="item.label"
              class="pl-4"
            >
              <button
                v-if="'items' in item && item.items.length"
                class="flex w-full justify-between"
                @click="setActiveSectionItems([item])"
              >
                {{ item.label }}
                <Icon name="common/chevron-right" />
              </button>
              <NuxtLink
                v-else
                class="break-words text-bt-blue-dark no-underline text-body-1"
                :href="item.href"
                external
                @click="onSectionItemClick(item)"
              >
                {{ item.label }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
      <MainHeaderMobileSidebarSearch />
    </div>
    <Icon
      name="common/close"
      class="fixed right-4 top-4 text-white"
      @click="emit('close')"
    />
  </div>
</template>
